import { useQueryClient } from '@tanstack/react-query';
import { HeadContent, Outlet, useRouter } from '@tanstack/react-router';
import { configure } from 'onedollarstats';
import { Suspense, useEffect } from 'react';
import { appConfig } from 'shared';
import { useCutTextTitle } from '~/hooks/use-cut-text-title';
import { useLazyComponent } from '~/hooks/use-lazy-component';
import { useOnlineManager } from '~/hooks/use-online-manager';
import { TooltipProvider } from '~/modules/ui/tooltip';
import { lazyNamed } from '~/utils/lazy-named';

const Devtools = __DEV_TOOLS__ ? lazyNamed(() => import('~/modules/common/devtools/devtools'), 'Devtools') : () => null;

const loadChatSupport = () => import('~/modules/common/gleap-support');

function NoChatSupport() {
  return null;
}

export function Root() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const isOnline = useOnlineManager();
  useCutTextTitle();

  // An error is not kept across navigations: a route that mounts a failed query again reads again.
  useEffect(
    () =>
      router.subscribe('onBeforeRouteMount', () =>
        queryClient.removeQueries({
          predicate: (query) =>
            query.state.status === 'error' && query.state.data === undefined && query.state.fetchStatus === 'idle' && query.getObserversCount() === 0,
        }),
      ),
    [router, queryClient],
  );

  const GleapSupport = useLazyComponent(
    () => (appConfig.has.chatSupport && isOnline ? loadChatSupport() : Promise.resolve({ GleapSupport: NoChatSupport })),
    'GleapSupport',
    5000,
  );

  useEffect(() => {
    configure({ trackLocalhostAs: null });
  }, []);

  // `timeout` keeps the tooltip delay group alive that long after one closes: the first waits, its neighbours are instant
  return (
    <TooltipProvider delay={300} timeout={400}>
      <HeadContent />
      <Outlet />
      <Suspense fallback={null}>{GleapSupport ? <GleapSupport /> : null}</Suspense>
      <Suspense fallback={null}>
        <Devtools />
      </Suspense>
    </TooltipProvider>
  );
}
